import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react'
import type { BulkInviteAlreadyKnownRow, BulkInviteInvalidRow, BulkInviteValidRow } from '@qse/api'
import { RotateCcw, Trash2 } from 'lucide-react'

import { useModalFullScreen } from '../../modal-fullscreen-context'

/** Only valid rows can be removed — the others aren't being invited anyway. */
type EntryKind = 'valid' | 'invalid' | 'already'

/** Chip keys map straight onto entry kinds; 'all' disables the filter. */
export type BulkInviteOutcomeFilter = 'all' | EntryKind

interface BreakdownTableProps {
  validRows: BulkInviteValidRow[]
  invalidRows: BulkInviteInvalidRow[]
  alreadyInvited: BulkInviteAlreadyKnownRow[]
  /** Rows the admin has removed on this screen — a subset of validRows' row numbers. */
  deletedRows: Set<number>
  /** Which outcome kind to show. Deleted rows are valid rows, so they stay under 'valid'. */
  filter: BulkInviteOutcomeFilter
  /** Toggle a valid row's removed state. */
  onToggleDelete: (row: number) => void
}

type Entry = {
  row: number
  email: string
  reason: string
  label: string
  className: string
  kind: EntryKind
}

const ALREADY_LABEL: Record<BulkInviteAlreadyKnownRow['status'], string> = {
  ALREADY_INVITED: 'Already invited',
  ALREADY_REGISTERED: 'Already registered',
}

/**
 * Every row's outcome, row-by-row — the Review step's detail.
 *
 * Valid rows, invalid rows, and already-known emails are merged into one list (the
 * admin cares about "what happens to each row", not our internal partition) but keep
 * distinct labels, because the remedies differ: a malformed row is fixable in the
 * file, an already-invited address is simply not a problem, and a valid row needs no
 * action at all.
 *
 * Valid rows also carry a delete control: removing one takes that user out of the
 * send (client-side only — the row numbers ride along on Send and the server excludes
 * them there). A removed row stays visible, struck through, with an Undo, so a misclick
 * costs nothing.
 */
export default function BreakdownTable({
  validRows,
  invalidRows,
  alreadyInvited,
  deletedRows,
  filter,
  onToggleDelete,
}: BreakdownTableProps) {
  // In the expanded modal the fixed cap would waste the screen the admin just
  // asked for — grow with the viewport but keep the Send/Back row reachable.
  const isFullScreen = useModalFullScreen()

  const entries: Entry[] = [
    ...validRows.map(item => ({
      row: item.row,
      email: item.email || '—',
      reason: '—',
      label: 'Valid',
      className: 'text-green-600 dark:text-green-400',
      kind: 'valid' as const,
    })),
    ...invalidRows.map(item => ({
      row: item.row,
      email: item.email || '—',
      reason: item.message,
      label: 'Invalid',
      className: 'text-red-500 dark:text-red-400',
      kind: 'invalid' as const,
    })),
    ...alreadyInvited.map(item => ({
      row: item.row,
      email: item.email,
      reason: item.message,
      label: ALREADY_LABEL[item.status],
      className: 'text-amber-600 dark:text-amber-400',
      kind: 'already' as const,
    })),
  ].sort((a, b) => a.row - b.row)

  if (!entries.length) return null

  const visibleEntries = filter === 'all' ? entries : entries.filter(e => e.kind === filter)

  // The unfiltered table hides itself when empty, but under an active filter an
  // absent table reads as a bug — say the filter matched nothing instead.
  if (!visibleEntries.length) {
    return (
      <div className="w-full rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-6 text-center text-sm text-gray-500 dark:text-[#AFAFAF]">
        No rows match this filter.
      </div>
    )
  }

  return (
    <div
      className={`w-full ${
        isFullScreen ? 'max-h-[60vh]' : 'max-h-64'
      } overflow-auto rounded-lg border border-gray-200 dark:border-[#2A2A2A]`}
    >
      <Table removeWrapper aria-label="Row-by-row breakdown" className="min-w-full">
        <TableHeader>
          <TableColumn>Row</TableColumn>
          <TableColumn>Email</TableColumn>
          <TableColumn>Outcome</TableColumn>
          <TableColumn>Reason</TableColumn>
          <TableColumn>{''}</TableColumn>
        </TableHeader>
        <TableBody>
          {visibleEntries.map(entry => {
            const isDeleted = entry.kind === 'valid' && deletedRows.has(entry.row)

            return (
              <TableRow key={`${entry.row}-${entry.email}`}>
                <TableCell className="whitespace-nowrap align-top text-sm">{entry.row}</TableCell>
                <TableCell
                  className={`align-top text-sm break-all ${
                    isDeleted ? 'line-through text-gray-400 dark:text-gray-500' : ''
                  }`}
                >
                  {entry.email}
                </TableCell>
                <TableCell className="whitespace-nowrap align-top">
                  <span
                    className={`text-sm font-medium ${
                      isDeleted ? 'text-gray-500 dark:text-gray-400' : entry.className
                    }`}
                  >
                    {isDeleted ? 'Deleted' : entry.label}
                  </span>
                </TableCell>
                <TableCell className="align-top text-sm text-gray-700 dark:text-gray-300">
                  {isDeleted ? '—' : entry.reason}
                </TableCell>
                <TableCell className="whitespace-nowrap align-top text-right">
                  {entry.kind === 'valid' &&
                    (isDeleted ? (
                      <Button
                        size="sm"
                        startContent={<RotateCcw className="w-3.5 h-3.5" />}
                        variant="light"
                        onPress={() => onToggleDelete(entry.row)}
                      >
                        Undo
                      </Button>
                    ) : (
                      <Button
                        isIconOnly
                        aria-label={`Remove row ${entry.row}`}
                        className="text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
                        size="sm"
                        variant="light"
                        onPress={() => onToggleDelete(entry.row)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    ))}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
